import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { SplitButton } from 'primereact/splitbutton'
import { useLocalstorageState } from 'rooks'

import { useToastContext } from '../../../../../../contexts'
import { useSplitViewContext } from '../../../../../../contexts/splitView'
import { requestAttachmentDownload } from '../../../../../../data/attachments/api'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { useUserGuideContext } from '../../../../../user-guide'
import { useEInvoiceById } from '../../../../data'

type DefaultAction = 'splitView' | 'inTab'

export const OpenPreview: React.FC = () => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { id: eInvoiceId } = useParams()
  const { data: eInvoice } = useEInvoiceById(eInvoiceId!)
  const {
    setActiveSplitView,
    setEInvoicePreviewUrl,
    setEInvoicePreviewFileName,
  } = useSplitViewContext()
  const { closeUserGuide, isUserGuideOpen } = useUserGuideContext()

  const [defaultAction, setDefaultAction] = useLocalstorageState<DefaultAction>(
    'EInvoicePreviewDefaultAction',
    'splitView',
  )

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const handleOpenInvoiceInTab = async () => {
    setDefaultAction('inTab')

    if (!eInvoice?.Attachments || eInvoice.Attachments.length === 0) {
      return
    }

    const attachment = eInvoice.Attachments.find(
      att => att.DocumentType === 'ReadableInvoice',
    )

    if (!attachment) {
      return
    }

    const fullUrl = attachment.DocServUri as string
    const mimeType = attachment.MimeType as string
    const fileName = attachment.FileName

    setIsRequestRunning(true)
    try {
      const attachmentBlob = await requestAttachmentDownload(
        fullUrl,
        mimeType,
        fileName,
      )

      const fileURL = window.URL.createObjectURL(attachmentBlob)
      const fileLink = document.createElement('a')
      fileLink.href = fileURL
      fileLink.target = '_blank'
      document.body.appendChild(fileLink)
      fileLink.click()
      document.body.removeChild(fileLink)
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        detail: errorMessage,
        summary: t(
          'eInvoiceDetails.title.openPreview.options.messages.error.summary',
          {
            name: fileName,
          },
        ),
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const handleOpenInvoiceInSplitView = async () => {
    setDefaultAction('splitView')

    if (!eInvoice?.Attachments || eInvoice.Attachments.length === 0) {
      return
    }

    const attachment = eInvoice.Attachments.find(
      att => att.DocumentType === 'ReadableInvoice',
    )

    if (!attachment) {
      return
    }

    const fullUrl = attachment.DocServUri as string
    const mimeType = attachment.MimeType as string
    const fileName = attachment.FileName

    setIsRequestRunning(true)

    try {
      // Close UserGuide if it's open to ensure only one panel is visible
      if (isUserGuideOpen) {
        closeUserGuide()
      }

      // Download attachment and create blob URL (same as Tab View)
      const attachmentBlob = await requestAttachmentDownload(
        fullUrl,
        mimeType,
        fileName,
      )

      const blobUrl = window.URL.createObjectURL(attachmentBlob)

      // Open E-Invoice Preview
      setEInvoicePreviewUrl(blobUrl)
      setEInvoicePreviewFileName(fileName)
      setActiveSplitView('eInvoicePreview')
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      const fileName = attachment.FileName

      showToast({
        severity: 'error',
        sticky: true,
        detail: errorMessage,
        summary: t(
          'eInvoiceDetails.title.openPreview.options.messages.error.summary',
          { name: fileName },
        ),
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const hasReadableInvoice = eInvoice?.Attachments?.some(
    att => att.DocumentType === 'ReadableInvoice',
  )

  const items = [
    {
      label:
        defaultAction === 'splitView'
          ? t(
              'eInvoiceDetails.title.openPreview.options.splitView.isStandardLabel',
            )
          : t('eInvoiceDetails.title.openPreview.options.splitView.label'),
      icon: 'pi pi-objects-column',
      command: handleOpenInvoiceInSplitView,
    },
    {
      label:
        defaultAction === 'inTab'
          ? t('eInvoiceDetails.title.openPreview.options.inTab.isStandardLabel')
          : t('eInvoiceDetails.title.openPreview.options.inTab.label'),
      icon: 'pi pi-external-link',
      command: handleOpenInvoiceInTab,
    },
  ]

  const handleDefaultAction = () => {
    if (defaultAction === 'splitView') {
      handleOpenInvoiceInSplitView()
    } else {
      handleOpenInvoiceInTab()
    }
  }

  return (
    <SplitButton
      label={t('eInvoiceDetails.title.openPreview.label')}
      onClick={handleDefaultAction}
      icon="pi pi-eye"
      loading={isRequestRunning}
      disabled={!hasReadableInvoice}
      model={items}
      outlined
    />
  )
}
